import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { Observable, map } from 'rxjs';

const API_URL = BASE_API_URL + 'auth';

export interface ICognitoUserStatus {
  email: string;
  status: string;
  enabled: boolean;
  verified: boolean;
}

const SIGN_IN_STATUS: Record<string, string> = {
  CONFIRMED: 'Account confirmed',
  UNCONFIRMED: 'Hasn’t confirmed their email',
  FORCE_CHANGE_PASSWORD: 'Must set a new password at next sign-in',
  RESET_REQUIRED: 'Must reset their password',
  EXTERNAL_PROVIDER: 'Signs in with Google or Apple',
  COMPROMISED: 'Password flagged as compromised',
  ARCHIVED: 'Archived',
};

/** The sign-in service's account status (e.g. FORCE_CHANGE_PASSWORD) in plain words. */
export function signInStatusLabel(status: string | null | undefined): string {
  const key = (status ?? '').toUpperCase();
  return SIGN_IN_STATUS[key] ?? (key ? key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' ') : 'Unknown');
}

/** The server catches Cognito failures and still answers 200, with `false` as the
 *  body. Treated as success, "Sign-in disabled" showed for an account that could
 *  still sign in, so a `false` reply becomes an error for the caller's catch. */
function okOrThrow(message: string) {
  return map((ok: boolean): true => {
    if (ok === false) throw Object.assign(new Error(message), { error: { message } });
    return true;
  });
}

/** Cognito account controls (admin-only on the server). Moved from the app's services/user-auth.service.ts. */
@Injectable({ providedIn: 'root' })
export class AdminAuthApiService {
  private readonly http = inject(HttpClient);

  status(email: string): Observable<ICognitoUserStatus> {
    return this.http.get<ICognitoUserStatus>(`${API_URL}/${encodeURIComponent(email)}`);
  }

  enable(email: string): Observable<true> {
    return this.http.post<boolean>(`${API_URL}/enable`, { email }).pipe(okOrThrow("Sign-in wasn't enabled"));
  }

  disable(email: string): Observable<true> {
    return this.http.post<boolean>(`${API_URL}/disable`, { email }).pipe(okOrThrow("Sign-in wasn't disabled"));
  }

  /** Emails the user a new temporary password (they choose a new one at next sign-in). */
  sendTemporaryPassword(email: string): Observable<true> {
    return this.http
      .post<boolean>(`${API_URL}/temporary-password`, { email })
      .pipe(okOrThrow('No temporary password was set'));
  }
}
